import { TFunction } from "i18n";
import { MeasurementTypes } from "models";
import { IBasicInfoViewModel } from "pages/MainPage/components/BasicInfoSection/viewModels";
import { ConverterBasicInfo } from "./types";

const CENTIMETERS_TO_INCHES_RATIO = 0.3937;
const KILOGRAMS_TO_POUNDS_RATIO = 2.204623;
const INCHES_PER_FOOT = 12;
const POUNDS_PER_STONE = 14;

function centimetersToInchesString(centimeters: number): string {
  const inches = Math.round(centimeters * CENTIMETERS_TO_INCHES_RATIO);

  return `${inches}"`;
}

function kilogramsToImperialString(
  kilograms: number,
  stonesName: string,
  poundsName: string
): string {
  const realPounds = Math.round(kilograms * KILOGRAMS_TO_POUNDS_RATIO);
  const pounds = realPounds % POUNDS_PER_STONE;
  const stones = (realPounds - pounds) / POUNDS_PER_STONE;

  return `${stones} ${stonesName} ${pounds} ${poundsName}`;
}

function centimetersToImperialString(
  centimeters: number,
  t: TFunction
): string {
  const realInches = Math.round(centimeters * CENTIMETERS_TO_INCHES_RATIO);
  const inches = realInches % INCHES_PER_FOOT;
  const feet = (realInches - inches) / INCHES_PER_FOOT;

  const feetText = t("common:label.foot", { count: feet });
  const inchPart = inches
    ? ` ${inches} ${t("common:label.inch", { count: inches })}`
    : "";

  return `${feet} ${feetText}${inchPart}`;
}

export function mapMeasurements(
  basicInfo: ConverterBasicInfo,
  t: TFunction
): Partial<IBasicInfoViewModel> {
  return basicInfo.measurements.reduce<Partial<IBasicInfoViewModel>>(
    (acc, measurement) => {
      switch (measurement.measurementType) {
        case MeasurementTypes.Chest:
          return {
            ...acc,
            chestSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.Waist:
          return {
            ...acc,
            waistSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.Hips:
          return {
            ...acc,
            hipsSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.InsideLeg:
          return {
            ...acc,
            insideLegSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.InsideArm:
          return {
            ...acc,
            insideArmSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.Collar:
          return {
            ...acc,
            collarSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.Hat:
          return {
            ...acc,
            hatSize: centimetersToInchesString(measurement.value),
          };
        case MeasurementTypes.Weight:
          return {
            ...acc,
            weight: kilogramsToImperialString(
              measurement.value,
              t("common:label.stones.short"),
              t("common:label.pounds.short")
            ),
          };
        case MeasurementTypes.Height:
          return {
            ...acc,
            height: `${centimetersToImperialString(measurement.value, t)} (${measurement.value}${t("common:label.centimeters.short")})`,
          };
        case MeasurementTypes.Shoes:
          return { ...acc, shoeSize: measurement.value.toString() };
        case MeasurementTypes.Dress:
          return { ...acc, dressSize: measurement.value.toString() };
        default:
          return acc;
      }
    },
    {}
  );
}
